// RITA-1504 (Nutzungserfassung Phase 2b) — Bestätigungsdialoge (Entwurf E).
//
// Genau ein Ereignis je Öffnung: bestätigt oder abgebrochen. Ein Riegel merkt sich, dass der
// Dialog entschieden ist — viele Dialoge rufen ihren onClose-Handler auch nach der Bestätigung
// auf, jedes Schließen zählte sonst zusätzlich als Abbruch. reset() beim Öffnen gibt den Riegel
// wieder frei.
//
// Der Haken hält keinen Zustand, der die Darstellung beeinflusst (nur eine Ref) und meldet
// nichts aus dem Inhalt des Dialogs — dialogProps kennt in batchSchema.ts nur `op`.
import { useCallback, useMemo, useRef } from 'react';
import type { UsageDialogName } from '../usageKeys';
import { usageTracker } from '../services/tracker';
import type { UsageRecord } from '../services/tracker';
import { buildDialogProps } from '../services/usageProps';
import type { DialogOp } from '../services/usageProps';

export interface DialogUsage {
  /** Der Dialog wurde bestätigt (die Aktion läuft). */
  confirmed(): void;
  /** Der Dialog wurde abgebrochen (auch Escape/Hintergrund, soweit onClose greift). */
  cancelled(): void;
  /** Beim Öffnen aufrufen: gibt den Riegel für die nächste Entscheidung frei. */
  reset(): void;
}

/**
 * `name` ist ein fester Dialogname aus usageKeys.ts; das Löschen einer Tabellenzeile meldet
 * zusätzlich unter '<tabellenname>.loeschen' und gibt den Namen deshalb als Zeichenkette mit.
 */
export const useDialogUsage = (
  name: UsageDialogName | string,
  record: UsageRecord = usageTracker.record,
): DialogUsage => {
  const resolvedRef = useRef(false);

  const resolve = useCallback(
    (op: DialogOp): void => {
      if (resolvedRef.current) return;
      resolvedRef.current = true;
      try {
        record({ type: 'dialog', name, props: buildDialogProps({ op }) });
      } catch {
        // Erfassung darf die Bedienung nicht stören.
      }
    },
    [name, record],
  );

  const confirmed = useCallback((): void => resolve('confirm'), [resolve]);
  const cancelled = useCallback((): void => resolve('cancel'), [resolve]);
  const reset = useCallback((): void => {
    resolvedRef.current = false;
  }, []);

  // Stabiles Ergebnis über die Darstellungen hinweg: Dialoge geben den Riegel in einem Effekt
  // beim Öffnen frei ('[open, dialogUsage]'). Ein bei jeder Darstellung neues Objekt würde
  // diesen Effekt bei jeder Darstellung erneut auslösen und mit ihm den Zustand des Dialogs
  // zurücksetzen.
  return useMemo(() => ({ confirmed, cancelled, reset }), [confirmed, cancelled, reset]);
};

export default useDialogUsage;
